import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, CalendarCheck, Calculator, HelpCircle, FileText,
  ListChecks, Clock, Settings, AlertTriangle, LogOut, Compass, MapPin, Home,
  Newspaper, Search, Mail, KeyRound, Users, Route, UserCircle, MessageCircle, Megaphone, Palette, Gift, Film, Hourglass, Navigation, Star, Handshake, Images,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

import Overview from "./sections/Overview";
import Submissions from "./sections/Submissions";
import ChatManager from "./sections/ChatManager";
import SocialManager from "./sections/SocialManager";
import BrandManager from "./sections/BrandManager";
import ReviewsManager from "./sections/ReviewsManager";
import ReferencesManager from "./sections/ReferencesManager";
import TripMediaManager from "./sections/TripMediaManager";
import Bookings from "./sections/Bookings";
import CalculatorSettings from "./sections/CalculatorSettings";
import FaqManager from "./sections/FaqManager";
import PageEditor from "./sections/PageEditor";
import QuizManager from "./sections/QuizManager";
import Availability from "./sections/Availability";
import ConsultationSettings from "./sections/ConsultationSettings";
import DisclaimersManager from "./sections/DisclaimersManager";
import DestinationsManager from "./sections/DestinationsManager";
import HomepageManager from "./sections/HomepageManager";
import MeridaManager from "./sections/MeridaManager";
import BlogManager from "./sections/BlogManager";
import SeoSettings from "./sections/SeoSettings";
import NewsletterManager from "./sections/NewsletterManager";
import AppointmentTypesManager from "./sections/AppointmentTypesManager";
import AppointmentsManager from "./sections/AppointmentsManager";
import TeamManager from "./sections/TeamManager";
import RelocationPlanner from "./sections/RelocationPlanner";
import ClientsManager from "./sections/ClientsManager";
import ReferralsManager from "./sections/ReferralsManager";
import VideosManager from "./sections/VideosManager";
import FollowUpsManager from "./sections/FollowUpsManager";
import ChecklistManager from "./sections/ChecklistManager";
import DriversManager from "./sections/DriversManager";
import TripsManager from "./sections/TripsManager";
import ChangePassword from "./ChangePassword";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, Comp: Overview },
  { id: "submissions", label: "Submissions", icon: Inbox, Comp: Submissions },
  { id: "chat", label: "Chat", icon: MessageCircle, Comp: ChatManager },
  { id: "social", label: "Social", icon: Megaphone, Comp: SocialManager },
  { id: "brand", label: "Brand", icon: Palette, Comp: BrandManager },
  { id: "bookings", label: "Bookings", icon: CalendarCheck, Comp: Bookings },
  { id: "appointments", label: "Appointments", icon: CalendarCheck, Comp: AppointmentsManager },
  { id: "drivers", label: "Drivers", icon: UserCircle, Comp: DriversManager },
  { id: "trips", label: "Pet Trips", icon: Navigation, Comp: TripsManager },
  { id: "reviews", label: "Reviews", icon: Star, Comp: ReviewsManager },
  { id: "references", label: "References", icon: Handshake, Comp: ReferencesManager },
  { id: "reviews-media", label: "Trip Media", icon: Images, Comp: TripMediaManager },
  { id: "appointment-types", label: "Appointment Types", icon: Clock, Comp: AppointmentTypesManager },
  { id: "planner", label: "Route Planner & Pricing", icon: Route, Comp: RelocationPlanner },
  { id: "clients", label: "Clients", icon: UserCircle, Comp: ClientsManager },
  { id: "referrals", label: "Referrals", icon: Gift, Comp: ReferralsManager },
  { id: "videos", label: "Videos", icon: Film, Comp: VideosManager },
  { id: "followups", label: "Follow-ups", icon: Hourglass, Comp: FollowUpsManager },
  { id: "checklist", label: "Checklist", icon: ListChecks, Comp: ChecklistManager },
  { id: "calculator", label: "Calculator", icon: Calculator, Comp: CalculatorSettings },
  { id: "faqs", label: "FAQs", icon: HelpCircle, Comp: FaqManager },
  { id: "pages", label: "Page content", icon: FileText, Comp: PageEditor },
  { id: "merida", label: "Mérida page", icon: MapPin, Comp: MeridaManager },
  { id: "homepage", label: "Homepage", icon: Home, Comp: HomepageManager },
  { id: "destinations", label: "Explore Mexico", icon: MapPin, Comp: DestinationsManager },
  { id: "blog", label: "Blog", icon: Newspaper, Comp: BlogManager },
  { id: "newsletter", label: "Newsletter", icon: Mail, Comp: NewsletterManager },
  { id: "seo", label: "SEO", icon: Search, Comp: SeoSettings },
  { id: "quiz", label: "Quiz", icon: ListChecks, Comp: QuizManager },
  { id: "availability", label: "Scheduling", icon: Clock, Comp: Availability },
  { id: "consultation", label: "Consultation", icon: Settings, Comp: ConsultationSettings },
  { id: "disclaimers", label: "Disclaimers", icon: AlertTriangle, Comp: DisclaimersManager },
  { id: "team", label: "Team / Admins", icon: Users, Comp: TeamManager },
  { id: "password", label: "Password", icon: KeyRound, Comp: ChangePassword },
];

export default function AdminDashboard() {
  const [active, setActive] = useState("overview");
  const { logout } = useAuth();
  const navigate = useNavigate();
  const ActiveComp = NAV.find((n) => n.id === active).Comp;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#EAF0EE] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-sand-200 bg-white">
        <div className="flex items-center gap-2 px-6 h-16 border-b border-sand-200">
          <img src="/brand/logo-mark.png" alt="Hola Movers" className="h-8 w-auto" />
          <span className="font-serif text-lg text-charcoal-800">Hola Movers</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`admin-nav-${n.id}`}
              onClick={() => setActive(n.id)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                active === n.id ? "bg-terracotta-light text-terracotta-700" : "text-charcoal-600 hover:bg-sand-100"
              }`}
            >
              <n.icon size={18} strokeWidth={1.5} />
              {n.label}
            </button>
          ))}
        </nav>
        <button
          data-testid="admin-logout"
          onClick={handleLogout}
          className="flex items-center gap-3 px-6 py-4 text-sm text-charcoal-500 hover:text-terracotta border-t border-sand-200"
        >
          <LogOut size={18} strokeWidth={1.5} /> Sign out
        </button>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-sand-200 overflow-x-auto">
        <div className="flex gap-1 p-2 min-w-max">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`admin-mnav-${n.id}`}
              onClick={() => setActive(n.id)}
              className={`rounded-lg px-3 py-2 text-xs font-medium whitespace-nowrap ${
                active === n.id ? "bg-terracotta-light text-terracotta-700" : "text-charcoal-600"
              }`}
            >
              {n.label}
            </button>
          ))}
          <button onClick={handleLogout} className="rounded-lg px-3 py-2 text-xs text-charcoal-500">Sign out</button>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 p-6 lg:p-10 pt-20 md:pt-10 overflow-y-auto">
        <div className="max-w-5xl">
          <ActiveComp />
        </div>
      </main>
    </div>
  );
}
